import { v4 as uuidv4 } from "uuid";
import { storage } from "@/utils/firebase";
import imageCompression from "browser-image-compression";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// Function to add an image watermark to an image
const addWatermark = async (file, watermarkImageUrl = "/watermark.png") => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas dimensions to match the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the main image on the canvas
      ctx.drawImage(img, 0, 0);

      // Load the watermark image
      const watermarkImg = new Image();
      watermarkImg.src = watermarkImageUrl;
      watermarkImg.crossOrigin = "anonymous";
      watermarkImg.onload = () => {
        // Set watermark to cover 100% of width and height
        const watermarkWidth = img.width;
        const watermarkHeight = img.height;

        // Set global alpha (opacity) to 30%
        ctx.globalAlpha = 0.3;

        // Draw watermark to cover the entire image
        ctx.drawImage(watermarkImg, 0, 0, watermarkWidth, watermarkHeight);

        // Reset global alpha for future operations
        ctx.globalAlpha = 1.0;

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: file.type }));
          } else {
            reject(new Error("Failed to create watermarked image."));
          }
        }, file.type);
      };
      watermarkImg.onerror = () =>
        reject(new Error("Failed to load watermark image."));
    };
    img.onerror = () => reject(new Error("Failed to load main image."));
  });
};

const uploadImage = async (file, folderName, userId) => {
  try {
    // Step 1: Compress the image
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1080,
      useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);

    // Step 2: Add a watermark to the compressed image
    const watermarkedFile = await addWatermark(compressedFile);

    // Step 3: Generate a unique file name
    const fileExtension = file.name.split(".").pop();
    const uniqueFileName = `${uuidv4()}.${fileExtension}`;
    const filePath = `${folderName}/${userId}/${uniqueFileName}`;

    // Step 4: Upload the file
    const storageRef = ref(storage, filePath);
    await uploadBytesResumable(storageRef, watermarkedFile);

    // Step 5: Get and return the download URL
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const handleImageUpload = async (files, folderName, userId) => {
  try {
    const uploadPromises = files.map((file) =>
      uploadImage(file, folderName, userId)
    );
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error("Error uploading images:", error);
    throw new Error("Failed to upload images. Please try again.");
  }
};
